import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, User, LogIn, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Header = ({ cartCount = 0 }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Accueil', href: '/' },
    {
      label: 'Nos Stores',
      href: '/nos-stores',
      dropdown: [
        { label: 'Tous les stores', href: '/nos-stores' },
        { label: 'Stores Bannes', href: '/nos-stores?cat=store-banne' },
        { label: 'Stores Véranda', href: '/nos-stores?cat=store-veranda' },
        { label: 'Stores à Projection', href: '/nos-stores?cat=store-projection' },
      ]
    },
    { label: 'Qui sommes-nous ?', href: '/qui-sommes-nous' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 bg-white border-b border-gray-200 transition-shadow duration-300 ${
          scrolled ? 'shadow-lg' : 'shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-24">
            {/* Logo + Phone */}
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center group">
                <img
                  src="/images/logo-banner.jpg"
                  alt="Stores Shop – Fabricant français depuis 1986"
                  className="h-20 w-auto object-contain group-hover:opacity-90 transition-opacity duration-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback texte si l'image échoue */}
                <div className="hidden flex-col">
                  <span className="text-3xl font-black tracking-tight text-[#1a1a1a] group-hover:text-orange-500 transition-colors duration-200">
                    Stores <span className="text-orange-500">Shop</span>
                  </span>
                  <span className="text-[10px] tracking-[0.3em] text-gray-500 uppercase font-medium -mt-1">
                    Fabricant Français • Depuis 1986
                  </span>
                </div>
              </Link>
              
              {/* Phone Number */}
              <a 
                href="tel:+33970703038" 
                className="hidden md:flex items-center gap-2 text-gray-700 hover:text-orange-500 transition-colors"
              >
                <Phone size={18} className="text-orange-500" />
                <span className="font-semibold">09 70 70 30 38</span>
              </a>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div key={link.href} className="relative group">
                  {link.dropdown ? (
                    <>
                      <button
                        className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          location.pathname === link.href
                            ? 'text-orange-500 bg-orange-50'
                            : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                        }`}
                        onMouseEnter={() => setDropdownOpen(link.label)}
                        onMouseLeave={() => setDropdownOpen(null)}
                      >
                        {link.label}
                        <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                      </button>
                      <div
                        onMouseEnter={() => setDropdownOpen(link.label)}
                        onMouseLeave={() => setDropdownOpen(null)}
                        className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 transition-all duration-200 ${
                          dropdownOpen === link.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                        }`}
                      >
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        location.pathname === link.href
                          ? 'text-orange-500 bg-orange-50'
                          : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {user ? (
                <Link
                  to="/mon-espace"
                  className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-all duration-200 shadow-md hover:shadow-orange-200"
                >
                  <User size={16} />
                  Mon espace pro
                </Link>
              ) : (
                <>
                  <Link
                    to="/connexion"
                    className="hidden md:flex items-center gap-2 text-gray-700 text-sm font-semibold hover:text-orange-500 transition-colors"
                  >
                    <LogIn size={16} /> Connexion
                  </Link>
                  <Link
                    to="/inscription"
                    className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-all duration-200 shadow-md hover:shadow-orange-200"
                  >
                    Espace Pro
                  </Link>
                </>
              )}
              <button className="relative p-2.5 rounded-lg hover:bg-gray-100 transition-colors">
                <ShoppingCart size={20} className="text-gray-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </button>
              {/* Mobile Menu Toggle */}
              <button
                className="lg:hidden p-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X size={22} className="text-gray-600" /> : <Menu size={22} className="text-gray-600" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            menuOpen ? 'max-h-[500px] border-t border-gray-100' : 'max-h-0'
          }`}
        >
          <nav className="bg-white px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-2 px-4 py-3 bg-orange-500 text-white text-sm font-semibold rounded-lg text-center hover:bg-orange-600 transition-colors"
            >
              Demander un devis
            </Link>
          </nav>
        </div>
      </header>

      {/* Floating Phone Button */}
      <a
        href="tel:+33970703038"
        className="fixed bottom-6 right-6 z-50 bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
        title="Appelez-nous : 09 70 70 30 38"
        aria-label="Appeler"
      >
        <Phone size={28} className="group-hover:animate-bounce" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
          09 70 70 30 38
        </span>
        <span className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-25"></span>
      </a>
    </>
  );
};

export default Header;
